import { useEffect, useRef, useState } from "react";
import MeetingMediaRoom from "@/components/MeetingMediaRoom";
import { useParams } from "react-router-dom";
import { API } from "@/contexts/AuthContext";

function newSecret() {
  const bytes = window.crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export default function MeetingGuestPage() {
  const { eventId } = useParams();
  const [invitation] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("invite") || "");
  const [name, setName] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const session = useRef("");
  const sequence = useRef(0);
  useEffect(() => {
    window.history.replaceState(window.history.state, "", window.location.pathname);
    const meta = document.createElement("meta");
    meta.name = "robots"; meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    return () => { sequence.current += 1; meta.remove(); };
  }, []);
  async function request(action) {
    const enter = action === "enter";
    if (busy) return;
    const current = ++sequence.current;
    setBusy(true); setError("");
    try {
      session.current ||= newSecret();
      const response = await fetch(API + "/meetings/guest/" + encodeURIComponent(eventId) + ("/" + action), {
        method: "POST", credentials: "omit", cache: "no-store", referrerPolicy: "no-referrer",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_token: session.current,
          ...(enter ? { invitation_token: invitation, display_name: name.trim() } : {}) }),
      });
      if (!response.ok) throw new Error(response.status === 503
        ? "Guest participation is not enabled yet."
        : response.status === 409 ? "The meeting changed. Please try again."
        : "This invitation or guest session is unavailable.");
      const data = await response.json();
      if (!["waiting", "admitted", "rejected", "left"].includes(data.status)) throw new Error("Guest status unavailable.");
      if (current === sequence.current) setStatus(data.status);
    } catch (failure) {
      if (current === sequence.current) setError(failure.message || "Unable to reach the meeting.");
    } finally { if (current === sequence.current) setBusy(false); }
  }
  return <main className="mx-auto max-w-lg space-y-5 p-6">
    <h1 className="text-2xl font-semibold">Zynthoro Meetings</h1>
    <p>Join as a guest. Your invitation gives no access to company files or the platform.</p>
    {error && <p role="alert">{error}</p>}
    {!status && <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); request("enter"); }}>
      <label className="block">Your name<input className="mt-1 block w-full rounded border p-2"
        required maxLength={100} value={name} disabled={busy} onChange={(event) => setName(event.target.value)} /></label>
      <button className="zy-btn-primary" disabled={busy || !name.trim() || !/^[A-Za-z0-9_-]{43}$/.test(invitation)}>
        {busy ? "Joining…" : "Enter waiting room"}
      </button>
      {!/^[A-Za-z0-9_-]{43}$/.test(invitation) && <p>Open the full invitation link supplied by your host.</p>}
    </form>}
    {status && <div className="space-y-3">
      <p role="status">{status === "waiting" ? "You are in the waiting room. Your host will admit you."
        : status === "admitted" ? "Your host has admitted you. You can request an audio and video connection below."
        : status === "left" ? "You have left the meeting."
        : "Your host has declined or ended your participation."}</p>
      {["waiting", "admitted"].includes(status) && <button className="rounded border px-4 py-2" disabled={busy} onClick={() => request("status")}>
        {busy ? "Checking…" : "Check admission"}
      </button>}
      {["waiting", "admitted"].includes(status) && <button className="ml-3 rounded border px-4 py-2"
        disabled={busy} onClick={() => request("leave")}>{busy ? "Please wait…" : "Leave meeting"}</button>}
    </div>}
    {status === "admitted" && <MeetingMediaRoom key={eventId} eventId={eventId} guestToken={session.current} />}
    <p className="text-sm text-slate-600">Keep this page open. Reloading clears this guest session from your browser. Treat your invitation link as private.</p>
  </main>;
}
